import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useCuentaYPermisos } from "../lib/permisosDelEquipo";

/**
 * Barra superior de "te quedaste sin créditos" — ADR-2026-07-30-barra-superior-creditos-home-ancho.
 *
 * Franja finita arriba de todo del **panel de contenido**, por encima del saludo
 * ("Buenos días, …"). Ocupa el 100% de ese panel, no del viewport: va montada
 * dentro de la página, así que la sidebar queda afuera sola —sin `fixed` ni
 * compensar `sidebarWidth`, que es lo que complica a las otras barras de la app
 * (ver `HomeTemplatesSection`)—.
 *
 * **También en mobile** desde ADR-2026-08-03-barra-creditos-reemplaza-upsell. Antes
 * era `hidden sm:block` porque en teléfono el aviso lo daba el banner de tarjeta
 * `upsell` de `HomeBanners`; ese banner ya no existe, así que si la barra siguiera
 * escondida el usuario se enteraría de que se quedó sin créditos recién al intentar
 * generar.
 *
 * El `%` sale del cupón real (`discountPercent`, ADR-2026-07-30-banner-home-habla-descuento-real): sin cupón vigente no se
 * promete descuento y el CTA baja a "Ver planes".
 */
export interface CreditsTopBarProps {
  /**
   * Qué se avisa: `sin` (saldo en cero) o `pocos` (por debajo del 5% de la
   * capacidad del plan). El criterio es único para todos los planes y vive en
   * `lib/creditsStatus` (ADR-2026-07-30-solo-criterio-saldo-toda-app).
   */
  estado: 'sin' | 'pocos';
  /** Saldo restante. Sólo se muestra en `pocos`, donde el número es el argumento. */
  restantes?: number;
  /** Descuento vigente en % entero, o `null` si no hay cupón activo. */
  discountPercent: number | null;
  /** Cierra la barra (el contenedor decide si lo recuerda). */
  onDismiss: () => void;
  /**
   * Qué hace el CTA. Sin esto navega a `/subscription`, que es el comportamiento
   * original. El shell de `/inicio` lo pasa para abrir el catálogo como overlay y
   * no sacar al usuario de la pantalla (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo).
   */
  onUpgrade?: () => void;
  /* Acá vivía `variant: "soft" | "brand"`. El tinte lavanda se borró en
     ADR-2026-08-03-barra-creditos-reemplaza-upsell: con la barra como ÚNICO aviso de
     saldo, dos tratamientos para el mismo mensaje era una diferencia sin criterio.
     Queda el azul de marca, que es el que ya usaba `/inicio`. */
  /**
   * Layout del contenedor: márgenes y radio. Lo pone el consumidor porque
   * depende del shell — a ras en la home (el panel de contenido llega al borde) y
   * con el margen y el radio de la sidebar flotante en `/inicio`.
   */
  className?: string;
}

/**
 * Las estrellitas del fondo (ADR-2026-08-04-la-barra-de-creditos-respira). Valores
 * FIJOS y no aleatorios a propósito: con `Math.random()` cada render las reubicaría,
 * así que un re-render por cambio de saldo o de cupón haría saltar todo el fondo.
 *
 * `x` es la posición dentro de la pista y, junto con `retraso`, es lo que separa a
 * las estrellas en el tiempo: dos con la misma duración pero distinto `x` nunca
 * cruzan juntas. `tam` va de 4 a 12px — más grandes que eso compiten con el texto.
 *
 * Los `x` están repartidos cada ~7% para que la barra nunca quede con un tramo
 * pelado: la pista arrastra a todas a la vez, así que dos estrellas pegadas en `x`
 * se ven pegadas siempre, no sólo al principio.
 */
const ESTRELLAS = [
  { tam: 10, x: '4%', y: '18%', dur: '13s', retraso: '0s', op: 0.55, titileo: '3.1s' },
  { tam: 6, x: '13%', y: '62%', dur: '19s', retraso: '-4s', op: 0.4, titileo: '4.3s', reversa: true },
  { tam: 4, x: '21%', y: '30%', dur: '24s', retraso: '-15s', op: 0.3, titileo: '5.7s' },
  { tam: 8, x: '29%', y: '82%', dur: '16s', retraso: '-9s', op: 0.45, titileo: '3.7s' },
  { tam: 12, x: '36%', y: '10%', dur: '10s', retraso: '-3s', op: 0.5, titileo: '2.6s', reversa: true },
  { tam: 5, x: '43%', y: '50%', dur: '21s', retraso: '-18s', op: 0.34, titileo: '4.6s' },
  { tam: 7, x: '50%', y: '24%', dur: '15s', retraso: '-11s', op: 0.46, titileo: '3.3s' },
  { tam: 5, x: '57%', y: '74%', dur: '22s', retraso: '-2s', op: 0.32, titileo: '5.2s', reversa: true },
  { tam: 9, x: '64%', y: '40%', dur: '12s', retraso: '-8s', op: 0.52, titileo: '2.9s' },
  { tam: 6, x: '71%', y: '14%', dur: '18s', retraso: '-6s', op: 0.38, titileo: '4.1s' },
  { tam: 11, x: '78%', y: '66%', dur: '11s', retraso: '-5s', op: 0.5, titileo: '2.8s', reversa: true },
  { tam: 4, x: '85%', y: '34%', dur: '23s', retraso: '-20s', op: 0.28, titileo: '6.1s' },
  { tam: 6, x: '92%', y: '84%', dur: '17s', retraso: '-13s', op: 0.38, titileo: '4.9s' },
  { tam: 7, x: '98%', y: '52%', dur: '14s', retraso: '-7s', op: 0.42, titileo: '3.4s', reversa: true },
];

/** Estrella de 4 puntas con los lados cóncavos: a 5–11px una de lados rectos se lee
 *  como un rombo. `currentColor` para que el color lo ponga la clase. */
function Estrellita({ className, style }: { className: string; style: CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0c1 6.2 4.8 10 11 11-6.2 1-10 4.8-11 11-1-6.2-4.8-10-11-11 6.2-1 10-4.8 11-11Z" />
    </svg>
  );
}

/** La capa decorativa completa. `aria-hidden` + `pointer-events-none`: no es
 *  contenido y no puede robarle el click al CTA ni a la cruz.
 *
 *  Exportada: la reusa la tarjeta de configuración de cuenta paga
 *  (`HomeSetupCard`), que comparte el mismo fondo azul. Copiar las 14 estrellas
 *  allá habría dejado dos cielos que se desincronizan al primer retoque. */
export function CampoDeEstrellas() {
  return (
    <div className="barra-creditos-estrellas" aria-hidden="true">
      {ESTRELLAS.map((e, i) => (
        <div
          key={i}
          className={`barra-creditos-estrella-pista${e.reversa ? ' barra-creditos-estrella-pista--reversa' : ''}`}
          style={{ '--dur': e.dur, '--retraso': e.retraso } as CSSProperties}
        >
          <Estrellita
            className="barra-creditos-estrella"
            style={
              {
                '--x': e.x,
                '--y': e.y,
                '--tam': `${e.tam}px`,
                '--estrella-op': e.op,
                '--titileo': e.titileo,
              } as CSSProperties
            }
          />
        </div>
      ))}
    </div>
  );
}

export function CreditsTopBar({
  estado,
  restantes,
  discountPercent,
  onDismiss,
  onUpgrade,
  className = "",
}: CreditsTopBarProps) {
  const navigate = useNavigate();
  const off = discountPercent !== null ? `${discountPercent}% OFF` : null;

  // "Mejorá tu plan" y no "Suscribite": el aviso ahora sale para cualquier plan
  // (ADR-2026-07-30-solo-criterio-saldo-toda-app), y a alguien que ya paga no se le pide que se suscriba.
  const gancho =
    estado === "sin"
      ? "Te quedaste sin créditos."
      : restantes === 1
        ? "Te queda 1 crédito."
        : `Te quedan ${restantes} créditos.`;
  const remate = estado === "sin" ? "y seguí creando." : "antes de frenar.";
  // Un miembro del equipo no cambia el plan: se le dice a quién pedírselo
  // (ADR-2026-10-19-permisos-por-miembro-del-equipo). El botón abre el mismo catálogo, que a
  // un miembro le muestra el nombre y el mail del dueño.
  const { esMiembro, dueno } = useCuentaYPermisos();

  return (
    // Sin `w-full`: es un bloque, ya ocupa el ancho disponible, y con `width:100%`
    // cualquier margen lateral que le pase el consumidor lo haría desbordar.
    //
    // `bg-brand-primary` queda debajo del degradé a propósito: es el
    // `background-color` sobre el que se dibuja el `background-image` de
    // `.barra-creditos-degrade` (index.css), así que si el gradiente no pintara
    // —o el consumidor recortara la barra— el texto blanco nunca queda sobre nada.
    // `relative`: es el marco de referencia de la capa de estrellas, que va absoluta
    // detrás del contenido (la fila de abajo también es `relative`, y al venir después
    // en el DOM pinta por encima sin necesidad de `z-index`).
    <div className={`barra-creditos-degrade relative bg-brand-primary ${className}`}>
      <CampoDeEstrellas />
      {/* MOBILE: el texto a la izquierda —envolviendo en las líneas que necesite— y
          a la derecha, arriba, el botón y la cruz
          (ADR-2026-08-04-el-cartel-de-creditos-alinea-sus-controles-arriba).
          Antes se apilaban (ADR-2026-08-04-la-barra-de-creditos-entra-en-un-telefono):
          el texto arriba y el botón abajo a la izquierda, lo que estiraba la franja y
          dejaba el CTA lejos del pulgar. `items-start` es lo que sube los dos controles
          a la primera línea del texto; `min-w-0` en el `<p>` es lo que le permite
          envolver en vez de empujar al botón fuera.

          DESKTOP (`sm:`): la fila centrada de siempre, con la cruz absoluta a la
          derecha; el `px-12` a los dos lados le reserva lugar sin correr el centro. */}
      {/* `z-10`: la fila va SIEMPRE por encima del campo de estrellas (`z-index: 0`),
          que cruza también la zona del texto y la del botón. */}
      <div className="relative z-10 flex items-center gap-3 px-4 py-3 sm:justify-center sm:px-12 sm:py-2.5">
        {/* Corto a propósito: en un panel de ~760px (viewport 1024 con la sidebar
            abierta) una línea más larga se parte en dos y la barra deja de
            leerse como una franja. */}
        <p className="min-w-0 flex-1 text-[14px] leading-[20px] text-white/90 sm:flex-none">
          <span className="font-bold">{gancho}</span>{" "}
          {esMiembro ? (
            <>Pedile {dueno ? `a ${dueno.nombre}` : "al administrador de la empresa"} que aumente el plan.</>
          ) : off ? (
            <>
              Mejorá tu plan con <span className="font-bold">{off}</span> {remate}
            </>
          ) : (
            <>Mejorá tu plan {remate}</>
          )}
        </p>
        <button
          type="button"
          onClick={() => (onUpgrade ? onUpgrade() : navigate("/subscription"))}
          // Radio 8px en mobile y 12px de `sm:` para arriba — pedido de diseño
          // (ADR-2026-08-04-el-cta-de-creditos-deja-de-ser-pastilla). Era `rounded-full`.
          // Van los valores en px y no los tokens (`rounded-sm` / `rounded-xl`) porque
          // la escala de radios está pisada en `index.css` y ahí `sm` son 8 y `xl` son
          // 12: leer "sm" y que sean 8px se presta a que alguien lo "corrija".
          className="inline-flex h-8 shrink-0 items-center justify-center rounded-[8px] bg-white px-4 text-[13px] font-semibold text-brand-primary shadow-sm transition-colors hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary sm:rounded-[12px]"
        >
          {esMiembro ? "Pedir más créditos" : off ? `Aprovechar ${off}` : "Ver planes"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar aviso"
          // En mobile es un control más de la fila, a media altura junto al CTA (el
          // `items-center` del contenedor); de `sm:` para arriba vuelve a ser absoluta,
          // para no correr el contenido centrado.
          className="shrink-0 rounded-full p-1 text-white/60 transition-colors hover:bg-white/15 hover:text-white sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default CreditsTopBar;
